/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-toolkit
 * (C) Copyright IBM Corp. 2019
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import TextInput from '@ba-ui-toolkit/textinput';
import Label from '@ba-ui-toolkit/label';
import VSpacer from '@ba-ui-toolkit/vspacer';
import FlexLayout from '@ba-ui-toolkit/flexlayout';
import { debounceInput, lodash } from '@ba-ui-toolkit/utils';

import styles from './Password.scss';

const pwdClassName = styles.pwdClassName.replace(/"/g, '');

const { omit } = lodash;

export default class Password extends Component {
	static propTypes = {
		...TextInput.propTypes,
		className: PropTypes.string,
		modifyPwd: PropTypes.bool,
		labelCurrent: PropTypes.string,
		errorTxtCurrent: PropTypes.string,
		labelPwd: PropTypes.string,
		errorTxtPwd: PropTypes.string,
		labelConfirm: PropTypes.string,
		errorTxtConfirm: PropTypes.string,
		successTxtConfirm: PropTypes.string,
		onPwdChange: PropTypes.func,
	};

	static defaultProps = {
		dir: 'ltr',
		labelCurrent: 'Current Password',
		errorTxtCurrent: 'Invalid password',
		labelPwd: 'Password',
		errorTxtPwd: 'Password cannot match current password',
		labelConfirm: 'Confirm Password',
		errorTxtConfirm: 'Password confirmation does not match',
		successTxtConfirm: 'Password is a match!',
		modifyPwd: false,
	};

	state = {
		currentPwd: '',
		pwd: '',
		confirmPwd: '',
	};

	_onCurrentPwdChange = v => {
		this.setState({ currentPwd: v }, () => {
			this._onChange();
		});
	};
	_onPwdChange = v => {
		this.setState({ pwd: v }, () => {
			this._onChange();
		});
	};
	_onConfirmPwdChange = v => {
		this.setState({ confirmPwd: v }, () => {
			this._onChange();
		});
	};

	_onChange() {
		const { pwd, currentPwd } = this.state;
		const isValid = this.isValid();
		this.setState({ isValid });
		this.props.onPwdChange && this.props.onPwdChange({ isValid, pwd, currentPwd });
	}

	// validate entire form
	isValid() {
		const { pwd, confirmPwd, currentPwd } = this.state;
		let isCurrPwdValid = false;
		let isNewPwdValid = false;

		if (confirmPwd.length > 0 && pwd.length > 0) {
			isNewPwdValid = confirmPwd === pwd;
			if (this.props.modifyPwd) {
				isCurrPwdValid = currentPwd.length > 0 && currentPwd !== pwd;
			} else {
				isCurrPwdValid = true;
			}

			return isNewPwdValid && isCurrPwdValid;
		}

		return false;
	}

	// compare passed in string with password then check if error message will show
	_showErrorMsg(otherPwd, isCurrentPwd) {
		const { pwd } = this.state;
		if (pwd.length > 0 && otherPwd.length > 0) {
			return this.props.modifyPwd && isCurrentPwd ? otherPwd === pwd : otherPwd !== pwd;
		}

		return false;
	}

	// show success msg only if password match and fields are not empty
	_getSuccessText() {
		const { pwd, confirmPwd } = this.state;
		if (pwd.length > 0 && confirmPwd.length > 0) {
			return this.props.successTxtConfirm;
		}

		return '';
	}

	// TODO: Add support for custom password strength checker
	// onCheckPassword = (password) => {
	//     return true;
	// }

	render() {
		const {
			className,
			labelCurrent,
			labelPwd,
			labelConfirm,
			modifyPwd,
			errorTxtCurrent,
			errorTxtPwd,
			errorTxtConfirm,
			...other
		} = this.props;
		const { pwd, currentPwd, confirmPwd } = this.state;
		const pwdInputProps = omit(other, 'onPwdChange', 'successTxtConfirm', 'type');

		return (
			<FlexLayout className={`${pwdClassName} ${className}`} direction="column">
				{modifyPwd && (
					<PasswordInput
						{...pwdInputProps}
						label={labelCurrent}
						value={currentPwd}
						htmlFor="currentPwd"
						validationErrorText={errorTxtCurrent}
						hasValidationError={false}
						onChange={this._onCurrentPwdChange}
					/>
				)}
				{modifyPwd && <VSpacer size={1} />}
				<PasswordInput
					{...pwdInputProps}
					delay={500}
					label={labelPwd}
					htmlFor="pwd"
					value={pwd}
					validationErrorText={errorTxtPwd}
					hasValidationError={this._showErrorMsg(currentPwd, true)}
					onChange={this._onPwdChange}
				/>
				<VSpacer size={1} />
				<PasswordInput
					{...pwdInputProps}
					delay={500}
					label={labelConfirm}
					value={confirmPwd}
					htmlFor="confirmPwd"
					successText={this._getSuccessText()}
					validationErrorText={errorTxtConfirm}
					hasValidationError={this._showErrorMsg(confirmPwd)}
					onChange={this._onConfirmPwdChange}
				/>
			</FlexLayout>
		);
	}
}

@debounceInput()
class PasswordInput extends Component {
	static propTypes = {
		...TextInput.propTypes,
		...Label.propTypes,
	};

	render() {
		const { label, htmlFor, ...other } = this.props;
		const textInputProps = omit(other, 'onClear', 'value', 'type');

		return (
			<div className={`${pwdClassName}__input`}>
				<Label type="caption" htmlFor={htmlFor} label={label} />
				<TextInput type="password" {...textInputProps} />
			</div>
		);
	}
}
